import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { isPaidRole } from '@/lib/billing-utils';

export interface SeatCount {
  creators: number;
  reviewers: number;
  approvers: number;
  viewers: number;
  totalPaid: number;
  totalUsers: number;
}

export function useSeatCount(companyId: string | undefined) {
  const [seatCount, setSeatCount] = useState<SeatCount>({
    creators: 0,
    reviewers: 0,
    approvers: 0,
    viewers: 0,
    totalPaid: 0,
    totalUsers: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }

    fetchSeatCount();
    
    // Set up real-time listener for user roles
    const channel = supabase
      .channel('seat-count-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_roles',
        },
        () => {
          fetchSeatCount();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'profiles',
          filter: `company_id=eq.${companyId}`,
        },
        () => {
          fetchSeatCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [companyId]);

  const fetchSeatCount = async () => {
    if (!companyId) return;

    try {
      setLoading(true);

      // Fetch all users with their roles for this company
      const { data: roles, error } = await supabase
        .from('user_roles')
        .select(`
          role,
          user_id,
          profiles!inner(company_id)
        `)
        .eq('profiles.company_id', companyId);

      if (error) throw error;

      // Count roles and users
      const counts: SeatCount = {
        creators: 0,
        reviewers: 0,
        approvers: 0,
        viewers: 0,
        totalPaid: 0,
        totalUsers: 0,
      };

      const uniqueUsers = new Set<string>();
      // Track users with paid roles (for billing - charge per user, not per role)
      const usersWithPaidRoles = new Set<string>();
      // Track users by their roles for counting
      const userRolesMap = new Map<string, Set<string>>();

      if (roles) {
        roles.forEach((roleEntry: any) => {
          const userId = roleEntry.user_id;
          const role = roleEntry.role;
          
          uniqueUsers.add(userId);

          // Initialize user's role set if not exists
          if (!userRolesMap.has(userId)) {
            userRolesMap.set(userId, new Set());
          }
          userRolesMap.get(userId)!.add(role);

          // Track if user has any paid role
          if (isPaidRole(role)) {
            usersWithPaidRoles.add(userId);
          }

          // Count role types for display (informational - counts how many users have each role)
          switch (role) {
            case 'creator':
              counts.creators++;
              break;
            case 'reviewer':
              counts.reviewers++;
              break;
            case 'approver':
              counts.approvers++;
              break;
            case 'viewer':
              // Don't count here - we'll count unique users with only viewer role below
              break;
          }
        });
      }

      // Count total unique users with paid roles (billing is per user, not per role)
      counts.totalPaid = usersWithPaidRoles.size;

      // Count viewers: unique users who have ONLY viewer role(s) or no roles
      const usersWithOnlyViewerRole = new Set<string>();
      userRolesMap.forEach((roleSet, userId) => {
        const roles = Array.from(roleSet);
        // If user has only viewer role(s) and no paid roles, they're a free viewer
        if (roles.every(r => r === 'viewer')) {
          usersWithOnlyViewerRole.add(userId);
        }
      });
      counts.viewers = usersWithOnlyViewerRole.size;

      // Handle users with no roles (shouldn't happen, but just in case)
      const { data: allProfiles } = await supabase
        .from('profiles')
        .select('id')
        .eq('company_id', companyId);

      if (allProfiles) {
        const totalProfiles = allProfiles.length;
        const usersWithoutRoles = totalProfiles - uniqueUsers.size;
        
        // If there are users without roles, count them as viewers (free)
        if (usersWithoutRoles > 0) {
          counts.viewers += usersWithoutRoles;
          counts.totalUsers = totalProfiles;
        } else {
          counts.totalUsers = uniqueUsers.size;
        }
      } else {
        counts.totalUsers = uniqueUsers.size;
      }

      setSeatCount(counts);
    } catch (err) {
      console.error('Error fetching seat count:', err);
    } finally {
      setLoading(false);
    }
  };

  return { seatCount, loading, refetch: fetchSeatCount };
}
